import React, { FC } from 'react';
import styled from 'styled-components';
import {
    SkeletonBlock,
    SkeletonFiltersRow,
    SkeletonFooterBar,
    SkeletonPageShell,
    SkeletonResponsiveGrid,
    SkeletonRow,
    SkeletonStack,
    SkeletonSurface,
    SkeletonTable,
    SkeletonTableRow,
    SkeletonTabs
} from 'src/components/widgets/skeleton';
import BulkImportFullscreenLayout from './BulkImportFullscreenLayout';

const BulkImportShell = styled(SkeletonPageShell).attrs({ $padding: '20px 0 48px' })``;

const BulkImportWizardShell = styled(SkeletonPageShell).attrs({ $padding: '20px 0 92px' })``;

const ActionCardSkeleton = styled(SkeletonSurface).attrs({ $padding: '20px 22px', $radius: '12px', $minHeight: '188px' })``;

const InfoSectionSkeleton = styled(SkeletonSurface).attrs({ $padding: '22px', $radius: '8px' })``;

const WizardStepBarSkeleton = styled(SkeletonSurface).attrs({ $padding: '16px 20px', $radius: '8px' })`
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 18px;

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const MetaGridSkeleton = styled.div`
    display: grid;
    grid-template-columns: 160px minmax(0, 1fr);
    gap: 8px 16px;
    min-width: 0;

    @media (max-width: 640px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const CounterGridSkeleton = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;

    @media (max-width: 720px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const HistoryTableSkeleton: FC = () => (
    <SkeletonTable>
        <SkeletonTableRow $columns='110px 110px 180px 200px 110px 1fr 60px' $mobileColumns='minmax(0, 1fr) 60px' $mobileBreakpoint='900px'>
            <SkeletonBlock $width='22px' $height='13px' />
            <SkeletonBlock $width='34px' $height='13px' />
            <SkeletonBlock $width='54px' $height='13px' />
            <SkeletonBlock $width='54px' $height='13px' />
            <SkeletonBlock $width='62px' $height='13px' />
            <SkeletonBlock $width='42px' $height='13px' />
            <SkeletonBlock $width='1px' $height='1px' />
        </SkeletonTableRow>
        {Array.from({ length: 7 }).map((_, index) => (
            <SkeletonTableRow key={index} $columns='110px 110px 180px 200px 110px 1fr 60px' $mobileColumns='minmax(0, 1fr) 60px' $mobileBreakpoint='900px'>
                <SkeletonBlock $width='64px' $height='15px' />
                <SkeletonBlock $width='72px' $height='22px' $radius='11px' />
                <SkeletonBlock $width='140px' $height='15px' />
                <SkeletonBlock $width='132px' $height='22px' $radius='11px' />
                <SkeletonBlock $width='72px' $height='15px' />
                <SkeletonBlock $width={index % 2 === 0 ? '220px' : '80px'} $height='15px' />
                <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
            </SkeletonTableRow>
        ))}
    </SkeletonTable>
);

export const BulkImportPageSkeleton: FC = () => (
    <BulkImportFullscreenLayout>
        <BulkImportShell aria-hidden='true'>
            <SkeletonBlock $height='82px' $radius='8px' $margin='0 0 16px' />
            <SkeletonResponsiveGrid $columns='repeat(auto-fit, minmax(260px, 1fr))' $gap='16px'>
                {Array.from({ length: 3 }).map((_, index) => (
                    <ActionCardSkeleton key={index}>
                        <SkeletonStack $gap='12px'>
                            <SkeletonBlock $width='44px' $height='44px' $radius='10px' />
                            <SkeletonBlock $width={index === 2 ? '110px' : '80px'} $height='18px' />
                            <SkeletonBlock $width='92%' $height='13px' />
                            <SkeletonBlock $width='76%' $height='13px' />
                            <SkeletonBlock $width='96px' $height='14px' $margin='4px 0 0' />
                        </SkeletonStack>
                    </ActionCardSkeleton>
                ))}
            </SkeletonResponsiveGrid>
            <InfoSectionSkeleton $padding='22px' $radius='8px'>
                <SkeletonStack $gap='14px'>
                    <SkeletonBlock $width='220px' $height='20px' />
                    <SkeletonBlock $width='min(720px, 100%)' $height='13px' />
                    <SkeletonBlock $width='min(620px, 100%)' $height='13px' />
                    <SkeletonBlock $height='120px' $radius='8px' />
                </SkeletonStack>
            </InfoSectionSkeleton>
        </BulkImportShell>
    </BulkImportFullscreenLayout>
);

export const BulkImportHistoryPageSkeleton: FC = () => (
    <BulkImportFullscreenLayout>
        <BulkImportShell aria-hidden='true'>
            <SkeletonBlock $height='82px' $radius='8px' $margin='0 0 16px' />
            <InfoSectionSkeleton>
                <SkeletonStack $gap='16px'>
                    <SkeletonStack $gap='8px'>
                        <SkeletonBlock $width='130px' $height='20px' />
                        <SkeletonBlock $width='min(520px, 100%)' $height='13px' />
                    </SkeletonStack>
                    <SkeletonFiltersRow
                        showSearch={false}
                        controlWidths={['180px', '180px', '150px', '150px', '72px', '94px', '92px']}
                        controlLabelWidths={['38px', '48px', '32px', '18px', null, null, null]}
                    />
                    <HistoryTableSkeleton />
                    <SkeletonRow $justify='flex-end' $gap='12px'>
                        <SkeletonBlock $width='160px' $height='14px' />
                        <SkeletonBlock $width='86px' $height='34px' $radius='6px' />
                        <SkeletonBlock $width='64px' $height='34px' $radius='6px' />
                    </SkeletonRow>
                </SkeletonStack>
            </InfoSectionSkeleton>
        </BulkImportShell>
    </BulkImportFullscreenLayout>
);

export const BulkImportWizardPageSkeleton: FC = () => (
    <BulkImportFullscreenLayout>
        <BulkImportWizardShell aria-hidden='true'>
            <WizardStepBarSkeleton>
                {Array.from({ length: 5 }).map((_, index) => (
                    <SkeletonRow key={index} $gap='10px'>
                        <SkeletonBlock $width='24px' $height='24px' $round />
                        <SkeletonBlock $width='min(110px, 100%)' $height='14px' />
                    </SkeletonRow>
                ))}
            </WizardStepBarSkeleton>
            <InfoSectionSkeleton>
                <SkeletonStack $gap='18px'>
                    <SkeletonBlock $width='180px' $height='20px' />
                    <SkeletonBlock $height='160px' $radius='10px' />
                    <SkeletonBlock $width='min(520px, 100%)' $height='13px' />
                    <SkeletonBlock $width='min(420px, 80%)' $height='13px' />
                </SkeletonStack>
            </InfoSectionSkeleton>
            <SkeletonFooterBar>
                <SkeletonBlock $width='82px' $height='36px' $radius='6px' />
                <SkeletonBlock $width='82px' $height='36px' $radius='6px' />
            </SkeletonFooterBar>
        </BulkImportWizardShell>
    </BulkImportFullscreenLayout>
);

export const BulkImportExportWizardPageSkeleton: FC = () => <BulkImportWizardPageSkeleton />;

const SetupTableSkeleton: FC = () => (
    <SkeletonTable>
        <SkeletonTableRow $columns='minmax(0, 1fr) minmax(0, 1fr) 140px 90px' $mobileColumns='minmax(0, 1fr) 90px' $mobileBreakpoint='760px'>
            <SkeletonBlock $width='64px' $height='13px' />
            <SkeletonBlock $width='120px' $height='13px' />
            <SkeletonBlock $width='118px' $height='13px' />
            <SkeletonBlock $width='48px' $height='13px' />
        </SkeletonTableRow>
        {Array.from({ length: 8 }).map((_, index) => (
            <SkeletonTableRow key={index} $columns='minmax(0, 1fr) minmax(0, 1fr) 140px 90px' $mobileColumns='minmax(0, 1fr) 90px' $mobileBreakpoint='760px'>
                <SkeletonBlock $width={index % 2 === 0 ? '180px' : '140px'} $height='15px' />
                <SkeletonBlock $width='100%' $height='28px' $radius='4px' />
                <SkeletonBlock $width='96px' $height='20px' $radius='4px' />
                <SkeletonBlock $width='62px' $height='20px' $radius='11px' />
            </SkeletonTableRow>
        ))}
    </SkeletonTable>
);

// Tabs strip + filters row + editable table — the data-dependent body of the
// setup page. Reused as the inner <Suspense> fallback while the root query loads,
// so it stays unwrapped (the page around it already provides the fullscreen chrome).
export const SetupTabsSkeleton: FC = () => (
    <InfoSectionSkeleton aria-hidden='true'>
        <SkeletonStack $gap='12px'>
            <SkeletonTabs tabWidths={['68px', '64px', '48px', '120px']} />
            <SkeletonFiltersRow showSearch controlWidths={['150px', '140px', '176px']} controlHeights={['20px', '20px', '28px']} />
            <SetupTableSkeleton />
            <SkeletonRow $justify='flex-end' $gap='12px'>
                <SkeletonBlock $width='220px' $height='14px' $margin='0 auto 0 0' />
                <SkeletonBlock $width='86px' $height='34px' $radius='6px' />
                <SkeletonBlock $width='80px' $height='34px' $radius='6px' />
            </SkeletonRow>
        </SkeletonStack>
    </InfoSectionSkeleton>
);

export const BulkImportSetupPageSkeleton: FC = () => (
    <BulkImportFullscreenLayout>
        <BulkImportShell aria-hidden='true'>
            <InfoSectionSkeleton>
                <SkeletonStack $gap='10px'>
                    <SkeletonBlock $width='150px' $height='20px' />
                    <SkeletonBlock $width='min(680px, 100%)' $height='13px' />
                    {Array.from({ length: 4 }).map((_, index) => (
                        <SkeletonBlock key={index} $width={`min(${[560, 620, 600, 520][index]}px, 100%)`} $height='13px' />
                    ))}
                </SkeletonStack>
            </InfoSectionSkeleton>
            <SetupTabsSkeleton />
        </BulkImportShell>
    </BulkImportFullscreenLayout>
);

export const BulkImportTaskDetailPageSkeleton: FC = () => (
    <BulkImportFullscreenLayout>
        <BulkImportShell aria-hidden='true'>
            <InfoSectionSkeleton>
                <SkeletonStack $gap='16px'>
                    <SkeletonRow>
                        <SkeletonBlock $width='104px' $height='22px' $radius='11px' />
                        <SkeletonBlock $width='70px' $height='18px' />
                    </SkeletonRow>
                    <MetaGridSkeleton>
                        {Array.from({ length: 6 }).map((_, index) => (
                            <React.Fragment key={index}>
                                <SkeletonBlock $width={index % 2 === 0 ? '88px' : '120px'} $height='14px' />
                                <SkeletonBlock $width={index % 2 === 0 ? '190px' : '260px'} $height='14px' />
                            </React.Fragment>
                        ))}
                    </MetaGridSkeleton>
                    <SkeletonRow $gap='8px'>
                        <SkeletonBlock $width='112px' $height='34px' $radius='6px' />
                        <SkeletonBlock $width='86px' $height='34px' $radius='6px' />
                    </SkeletonRow>
                </SkeletonStack>
            </InfoSectionSkeleton>
            <InfoSectionSkeleton>
                <SkeletonStack $gap='16px'>
                    <SkeletonBlock $width='96px' $height='20px' />
                    <SkeletonBlock $height='12px' $radius='6px' />
                    <CounterGridSkeleton>
                        {Array.from({ length: 4 }).map((_, index) => (
                            <SkeletonBlock key={index} $height='72px' $radius='8px' />
                        ))}
                    </CounterGridSkeleton>
                    <SkeletonTable>
                        {Array.from({ length: 5 }).map((_, index) => (
                            <SkeletonTableRow key={index} $columns='1fr 160px 80px' $mobileColumns='minmax(0, 1fr)'>
                                <SkeletonBlock $width={index === 0 ? '60px' : '130px'} $height='14px' />
                                <SkeletonBlock $width='90px' $height='14px' />
                                <SkeletonBlock $width='42px' $height='14px' />
                            </SkeletonTableRow>
                        ))}
                    </SkeletonTable>
                </SkeletonStack>
            </InfoSectionSkeleton>
        </BulkImportShell>
    </BulkImportFullscreenLayout>
);
